/*************************************** THIRD-PARTY MODULES **************************************/
import React from 'react';
import {withRouter} from 'react-router-dom';
import {inject, observer} from 'mobx-react';
import {RouterProps, parseQueryParams} from 'mad-utils/lib/react';

/**************************************** PROJECT MODULES *****************************************/
import {RequestCallback} from '@canimmunize/javelinscript';
import {EmailVerificationBaseCardView} from './email-verification-base-card-view';
import {EmailVerificationSuccessCardView} from './email-verification-success-card-view';
import {EmailVerificationFailCardView} from './email-verification-fail-card-view';
import {StoreAndConfigProps} from '../../../../types/types';
import {withToastNotifications, ToasterProps} from '../../../../hocs/with-toast-notifications';
import {asLaddaController, LaddaProps} from '../../../../hocs/as-ladda-controller';

/**************************************** TYPE DEFINITIONS ****************************************/
interface EmailVerificationCardContainerProps
    extends StoreAndConfigProps,
        ToasterProps,
        LaddaProps,
        RouterProps {
    renderComponent: 'base' | 'fail' | 'success';
}

interface EmailVerificationCardContainerState {
    success: boolean;
}

/********************************************* EXPORT *********************************************/
/**
 * (Higher-order component) Factory to create container components for email verification cards
 *
 * Reason for the odd structure: to feed the component to React-Router with
 * props taken from the closure in the auth-panel routing file
 */
export const EmailVerificationCardContainer = (props: EmailVerificationCardContainerProps) => {
    /**
     * Container shared by all email verification cards
     */
    @inject('appState', 'config')
    @observer
    @withRouter
    @withToastNotifications
    @asLaddaController
    class EmailVerificationCardContainerUnwrapped extends React.Component<
        EmailVerificationCardContainerProps,
        EmailVerificationCardContainerState
    > {
        state = {success: false};

        /**
         * Navigates to login page without full page reload
         * Leaves history intact (for back behaviour)
         */
        navToLogin = () => {
            const loginPath = this.props.config.routes.login;
            this.props.history.push(loginPath);
        };

        /**
         * Re-sends verification email to the email address in the state store
         * (extracted from query params)
         */
        sendVerificationEmail = () => {
            this.props.laddaOn();

            const {accountStore, syncManager} = this.props.appState;
            const {addNotification} = this.props;
            const qps = parseQueryParams<{email?: string}>(window.location.search);

            syncManager.resendVerificationEmail(
                qps.email || accountStore.email,
                new RequestCallback({
                    onSuccess: success => {
                        this.props.laddaOff();
                        this.setState({success: true});
                        addNotification('success', success.message);
                    },
                    onError: err => {
                        this.props.laddaOff();
                        addNotification('error', err.message);
                    },
                })
            );
        };

        /**
         * Render specific email verification card for current status (based on
         * the value of the 'renderComponent' prop)
         */
        render() {
            switch (props.renderComponent) {
                case 'fail':
                    return (
                        <EmailVerificationFailCardView
                            sendVerificationEmail={this.sendVerificationEmail}
                            email={this.props.appState.accountStore.email}
                            success={this.state.success}
                            isLoading={this.props.isLoading}
                        />
                    );

                case 'success':
                    return (
                        <EmailVerificationSuccessCardView
                            navToLogin={this.navToLogin}
                            platform={this.props.appState.queryParamStore.platformQueryParam}
                        />
                    );

                case 'base':
                default:
                    return (
                        <EmailVerificationBaseCardView
                            sendVerificationEmail={this.sendVerificationEmail}
                        />
                    );
            }
        }
    }

    return EmailVerificationCardContainerUnwrapped;
};
